import React, { useState } from 'react';
import { Carousel } from '../Carousel';
import {
  StyledContainer,
  StyledElementContainer,
  StyledElementImage,
  StyledElementLayer,
  StyledElementTitle,
  StyledElementTitleHover,
  StyledNavLink,
  styles,
} from './CategoryShowcase.styles';
import { SkeletonThumbnail } from '@shared/components/Skeleton';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { NavLink } from '@react-router';
import { useTranslation } from '@shared/core';
import { useCatalogRouting } from '../../useCatalogRouting';
import { ExplodedCategoryProps } from '../../routes/CatalogProductsIndex/components/ExplodedCategory/ExplodedCategory';
import { BrandCard, BrandPromo } from '@apps/registry/admin/components/BrandCard/BrandCard';
import { Box, Flex } from '@withjoy/joykit';
import { CatalogDepartmentDictionaryEntry } from '../../state/Catalog';
import { pxToRem } from '@withjoy/joykit/theme';
import { boxShadowValuesV2 } from '@shared/utils/style/dropShadows';

export type Category = {
  id: string;
  title: string;
  publicUrlPath: string;
  image: string;
  isFeatured: boolean;
};

export type VariantType = 'default' | 'overview';

export type CategoryShowcaseProps = {
  categories: Category[];
  showSkeleton?: boolean;
  disablePeek?: boolean;
  variant?: VariantType;
  onBrowseCategoryClick?: ExplodedCategoryProps['onBrowseCategoryClick'];
  selectedBrandPromo?: BrandPromo | null;
  selectedCategory?: Readonly<CatalogDepartmentDictionaryEntry> | null;
};

export type CategoryShowcaseElementProps = {
  category: Category;
  variant?: VariantType;
  heightPercent: string;
  onBrowseCategoryClick?: ExplodedCategoryProps['onBrowseCategoryClick'];
  selectedCategory?: Readonly<CatalogDepartmentDictionaryEntry> | null;
};

export const CategoryShowcase = ({
  categories,
  showSkeleton = false,
  disablePeek: disablePeekProp = false,
  variant = 'default',
  onBrowseCategoryClick,
  selectedCategory,
  selectedBrandPromo,
}: CategoryShowcaseProps) => {
  const isMobile = useIsMobileScreen();
  const heightPercent: string = isMobile ? '123.32%' : variant === 'overview' ? '111%' : '58.75%';

  const showBrandCard = !!selectedBrandPromo;
  const disablePeek = disablePeekProp || (showBrandCard && !isMobile);
  const brandSpan = showBrandCard ? (isMobile ? 1 : 2) : 0;

  return (
    <Flex
      gap={5}
      paddingX={disablePeek ? { lg1: 10, md3: pxToRem(40), _: 6, xxl: pxToRem(88) } : 0}
    >
      <StyledContainer
        flexBasis="100%"
        __css={styles.container}
      >
        <Carousel
          totalNumberOfItems={categories.length + brandSpan}
          enablePeek={!disablePeek}
          itemSpacing={isMobile ? 8 : 16}
          peekMobileExtraSize={40}
          showSkeleton={showSkeleton}
          placeholder={
            <StyledElementContainer
              __css={styles.elementContainer}
              display="flex"
            >
              <SkeletonThumbnail
                __css={styles.elementImage(heightPercent)}
                flexGrow={1}
              />
            </StyledElementContainer>
          }
          columns={variant === 'overview' ? [2, 4, 5, 5, 5] : [2, 4, 5, 6, 7]}
        >
          {showBrandCard && (
            <Carousel.Item columnSpan={brandSpan}>
              <BrandCard
                showShopButton={false}
                brandPromo={selectedBrandPromo}
                brandCardType="categoryCarousel"
              />
            </Carousel.Item>
          )}
          {categories.map(category => {
            return (
              <Carousel.Item key={category.id}>
                <Box boxShadow={boxShadowValuesV2.xxxl}>
                  <CategoryShowcaseElement
                    selectedCategory={selectedCategory}
                    category={category}
                    variant={variant}
                    heightPercent={heightPercent}
                    onBrowseCategoryClick={onBrowseCategoryClick}
                  />
                </Box>
              </Carousel.Item>
            );
          })}
        </Carousel>
      </StyledContainer>
    </Flex>
  );
};

const CategoryShowcaseElement = ({ category, variant = 'default', heightPercent, onBrowseCategoryClick, selectedCategory }: CategoryShowcaseElementProps) => {
  const { t } = useTranslation('catalogRegistry');
  const { buttonHover } = t('categoryPage');
  const { getToCategoryPath } = useCatalogRouting();
  const { title, publicUrlPath, image } = category;
  const layerColor: string = variant === 'overview' ? 'linear-gradient(0deg, rgba(0, 0, 0, 0.30) 0%, rgba(0, 0, 0, 0.30) 100%)' : 'rgba(0,0,0,0.3)';
  const layerActiveColor: string = variant === 'overview' ? 'linear-gradient(0deg, rgba(0, 0, 0, 0.30) 0%, rgba(0, 0, 0, 0.30) 100%)' : 'rgba(0,0,0,0.2)';
  const [isHovered, setIsHovered] = useState(false);

  return selectedCategory?.isPopularStoreCategory ? (
    <StyledElementContainer
      __css={styles.elementLogoContainer}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <StyledNavLink
        as={NavLink}
        __css={styles.navLink}
        to={getToCategoryPath(publicUrlPath)}
        onClick={() => onBrowseCategoryClick?.(category, 'categoryShowcase', true)}
      >
        <StyledElementImage
          backgroundImage={`url(${image})`}
          __css={styles.elementLogo(heightPercent)}
        />
      </StyledNavLink>
    </StyledElementContainer>
  ) : (
    <StyledElementContainer
      __css={styles.elementContainer}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <StyledNavLink
        as={NavLink}
        __css={styles.navLink}
        to={getToCategoryPath(publicUrlPath)}
        onClick={() => onBrowseCategoryClick?.(category, 'categoryShowcase', true)}
      >
        <StyledElementTitle
          __css={styles.elementTitle(variant)}
          typographyVariant={['hed1', 'hed5', 'hed5']}
          opacity={isHovered ? 0 : 1}
        >
          {title}
        </StyledElementTitle>
        <StyledElementTitleHover
          as="span"
          __css={styles.elementTitleHover}
          typographyVariant="button2"
          opacity={isHovered ? 1 : 0}
        >
          {buttonHover()}
        </StyledElementTitleHover>
        <StyledElementLayer __css={styles.elementLayer(layerColor, layerActiveColor)} />
        <StyledElementImage
          backgroundImage={`url(${image})`}
          __css={styles.elementImage(heightPercent)}
        />
      </StyledNavLink>
    </StyledElementContainer>
  );
};
